import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

function isIosDevice() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isMobileUa() {
  return /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent);
}

const PwaInstallContext = createContext(null);

export function PwaInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [standalone, setStandalone] = useState(() =>
    typeof window !== 'undefined' ? isInStandaloneMode() : false
  );
  const [isIos, setIsIos] = useState(false);
  const [isMobileUaState, setIsMobileUaState] = useState(false);

  useEffect(() => {
    setStandalone(isInStandaloneMode());
    setIsIos(isIosDevice());
    setIsMobileUaState(isMobileUa());

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
      setDismissed(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const canOfferInstall = useMemo(() => {
    if (dismissed || standalone) return false;
    return Boolean(deferredPrompt) || isIos || isMobileUaState;
  }, [deferredPrompt, dismissed, standalone, isIos, isMobileUaState]);

  const install = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return { outcome };
  }, [deferredPrompt]);

  const dismiss = useCallback(() => setDismissed(true), []);

  const value = useMemo(
    () => ({
      deferredPrompt,
      canOfferInstall,
      install,
      dismiss,
      isIos,
      hasNativeInstallPrompt: Boolean(deferredPrompt),
    }),
    [deferredPrompt, canOfferInstall, install, dismiss, isIos]
  );

  return (
    <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>
  );
}

export function usePwaInstall() {
  const ctx = useContext(PwaInstallContext);
  if (!ctx) {
    throw new Error('usePwaInstall must be used within PwaInstallProvider');
  }
  return ctx;
}
